import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { CheckCircle2, XCircle, AlertTriangle, RefreshCw, Link2Off, Send, Facebook } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("access_token") || ""}`,
});

export const SocialTab = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [appForm, setAppForm] = useState({
    app_id: "1750413802786333",
    app_secret: "",
  });
  const [oauthPages, setOauthPages] = useState([]); // pages list from OAuth flow
  const [selectedPageId, setSelectedPageId] = useState("");
  const [pub, setPub] = useState({
    image_url: "",
    caption: "",
    product_url: "",
    to_facebook: true,
    to_instagram: true,
  });
  const [pubResult, setPubResult] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [manualPageId, setManualPageId] = useState("");
  const [manualBinding, setManualBinding] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [productSku, setProductSku] = useState("");
  const [productPublishing, setProductPublishing] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/admin/social/history?limit=30`, { headers: authHeaders() });
      setHistory(r.data?.items || []);
    } catch (e) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handlePublishProduct = async (e) => {
    e.preventDefault();
    const sku = (productSku || "").trim();
    if (!sku) {
      setMsg({ ok: false, text: "Inserisci lo SKU del prodotto da pubblicare." });
      return;
    }
    setProductPublishing(true);
    setMsg(null);
    try {
      const r = await axios.post(`${API}/admin/social/publish-product`,
        { sku, to_facebook: true, to_instagram: true },
        { headers: authHeaders() }
      );
      const fb = r.data?.results?.facebook;
      const ig = r.data?.results?.instagram;
      setMsg({
        ok: true,
        text: `Pubblicato ${sku}: FB=${fb?.success ? "✅" : "❌ " + (fb?.error || "")} · IG=${ig?.success ? "✅" : "❌ " + (ig?.error || "")}`,
      });
      setProductSku("");
      await loadHistory();
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || err.message });
    } finally {
      setProductPublishing(false);
    }
  };

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/social/status`, { headers: authHeaders() });
      setStatus(r.data);
    } catch (e) {
      setStatus({ connected: false, error: e?.response?.data?.detail || e.message });
    }
  }, []);

  const loadOauthPages = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/social/oauth/pages`, { headers: authHeaders() });
      setOauthPages(r.data?.pages || []);
    } catch (e) {
      setOauthPages([]);
    }
  }, []);

  // initial load + handle OAuth return params
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/social/status`, { headers: authHeaders() });
        if (alive) setStatus(r.data);
      } catch (e) {
        if (alive) setStatus({ connected: false });
      }
      // Load history on mount (best-effort)
      try {
        const rh = await axios.get(`${API}/admin/social/history?limit=30`, { headers: authHeaders() });
        if (alive) setHistory(rh.data?.items || []);
      } catch (e) { /* ignore */ }
      // Detect OAuth callback params in URL/hash
      try {
        const url = new URL(window.location.href);
        const qp = url.searchParams;
        if (qp.get("oauth_error")) {
          if (alive) setMsg({ ok: false, text: `Errore OAuth: ${qp.get("oauth_error")}` });
          qp.delete("oauth_error");
          window.history.replaceState({}, "", url.pathname + (qp.toString() ? "?" + qp.toString() : "") + url.hash);
        } else if (qp.get("oauth_ok")) {
          if (alive) setMsg({ ok: true, text: "Login Facebook completato! Seleziona la Pagina." });
          try {
            const r2 = await axios.get(`${API}/admin/social/oauth/pages`, { headers: authHeaders() });
            if (alive) setOauthPages(r2.data?.pages || []);
            const auto = qp.get("auto_select");
            if (auto && alive) {
              await axios.post(`${API}/admin/social/oauth/select-page`, { page_id: auto }, { headers: authHeaders() });
              const r3 = await axios.get(`${API}/admin/social/status`, { headers: authHeaders() });
              if (alive) setStatus(r3.data);
            }
          } catch (e) { /* ignore */ }
          qp.delete("oauth_ok");
          qp.delete("auto_select");
          qp.delete("pages_count");
          window.history.replaceState({}, "", url.pathname + (qp.toString() ? "?" + qp.toString() : "") + url.hash);
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  const handleSaveApp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg(null);
    try {
      await axios.post(`${API}/admin/social/oauth/save-app`, appForm, { headers: authHeaders() });
      setMsg({ ok: true, text: "App ID + Secret salvati. Adesso clicca 'Connetti con Facebook'." });
      setAppForm({ ...appForm, app_secret: "" });
      await loadStatus();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleOauthStart = async () => {
    setLoading(true);
    setMsg(null);
    try {
      const r = await axios.get(`${API}/admin/social/oauth/start`, { headers: authHeaders() });
      // Redirect in same tab — Facebook will redirect back here
      window.location.href = r.data.oauth_url;
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message });
      setLoading(false);
    }
  };

  const handleSelectPage = async () => {
    if (!selectedPageId) {
      setMsg({ ok: false, text: "Seleziona una pagina prima." });
      return;
    }
    setLoading(true);
    setMsg(null);
    try {
      const r = await axios.post(`${API}/admin/social/oauth/select-page`,
        { page_id: selectedPageId },
        { headers: authHeaders() }
      );
      setMsg({ ok: true, text: `Collegato: ${r.data.page_name} → IG: ${r.data.ig_username ? "@" + r.data.ig_username : "—"}` });
      setOauthPages([]);
      setSelectedPageId("");
      await loadStatus();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Scollegare l'account social? Dovrai ri-autenticarti con Facebook.")) return;
    try {
      await axios.post(`${API}/admin/social/disconnect`, {}, { headers: authHeaders() });
      setMsg({ ok: true, text: "Scollegato." });
      setOauthPages([]);
      await loadStatus();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    }
  };

  const handleManualBind = async () => {
    const pid = (manualPageId || "").trim();
    if (!pid || !/^\d{5,}$/.test(pid)) {
      setMsg({ ok: false, text: "Inserisci un Page ID numerico valido (es. 1147473458452745)." });
      return;
    }
    setManualBinding(true);
    setMsg(null);
    try {
      const r = await axios.post(`${API}/admin/social/manual-bind`,
        { page_id: pid },
        { headers: authHeaders() }
      );
      setMsg({
        ok: true,
        text: `✅ Collegato manualmente: ${r.data.page_name} → IG: ${r.data.ig_username ? "@" + r.data.ig_username : "non trovato"}`,
      });
      setOauthPages([]);
      setSelectedPageId("");
      setManualPageId("");
      await loadStatus();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally {
      setManualBinding(false);
    }
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    setPublishing(true);
    setPubResult(null);
    try {
      const r = await axios.post(`${API}/admin/social/publish`, pub, { headers: authHeaders() });
      setPubResult(r.data.results);
    } catch (e) {
      setPubResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setPublishing(false);
    }
  };

  const isConnected = status?.connected && status?.page_id;
  const isExpired = status?.user_token_expired;
  const appConfigured = !!status?.app_id;

  return (
    <div className="space-y-6 max-w-4xl mx-auto py-6" data-testid="social-tab">
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span>📣 Social Auto-Publish — Facebook + Instagram</span>
            <Button variant="outline" size="sm" onClick={() => { loadStatus(); loadOauthPages(); }} data-testid="social-refresh-btn">
              <RefreshCw className="h-3 w-3 mr-1" /> Aggiorna
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status block */}
          {isConnected ? (
            <Alert className="bg-emerald-950 border-emerald-700">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              <AlertDescription className="text-emerald-100">
                <div className="space-y-1 text-sm">
                  <div><strong>Pagina FB:</strong> {status.page_name} <code className="text-xs text-slate-400">({status.page_id})</code></div>
                  <div><strong>Instagram:</strong> {status.ig_username ? `@${status.ig_username}` : <span className="text-amber-300">non collegato alla pagina</span>}</div>
                  <div><strong>Token scade:</strong> {status.user_token_expires_at ? new Date(status.user_token_expires_at).toLocaleDateString("it-IT") : "—"} {isExpired && <Badge variant="destructive" className="ml-2">SCADUTO</Badge>}</div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={handleOauthStart} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="social-renew-btn">
                    🔄 {loading ? "Reindirizzamento..." : "Rinnova connessione"}
                  </Button>
                  <Button variant="destructive" size="sm" onClick={handleDisconnect} data-testid="social-disconnect-btn">
                    <Link2Off className="h-3 w-3 mr-1" /> Scollega
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-1">Il rinnovo ri-autorizza con Facebook senza perdere la configurazione (altri 60 giorni).</p>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="bg-amber-950 border-amber-700">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <AlertDescription className="text-amber-100">
                Account social non collegato. Segui i 2 step qui sotto.
              </AlertDescription>
            </Alert>
          )}

          {msg && (
            <Alert className={msg.ok ? "bg-emerald-950 border-emerald-700" : "bg-rose-950 border-rose-700"}>
              {msg.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-rose-400" />}
              <AlertDescription className={msg.ok ? "text-emerald-100" : "text-rose-100"}>{msg.text}</AlertDescription>
            </Alert>
          )}

          {/* STEP 1: App ID + App Secret */}
          {!isConnected && (
            <div className="bg-slate-800 border border-slate-700 rounded p-4 space-y-3">
              <h3 className="text-white font-medium flex items-center gap-2">
                <span className="text-xs bg-amber-600 text-white px-2 py-0.5 rounded">STEP 1</span>
                Salva App ID + App Secret {appConfigured && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
              </h3>
              <form onSubmit={handleSaveApp} className="space-y-3" data-testid="social-save-app-form">
                <div>
                  <label className="text-xs text-slate-400 uppercase block mb-1">App ID</label>
                  <Input
                    value={appForm.app_id}
                    onChange={(e) => setAppForm({ ...appForm, app_id: e.target.value })}
                    placeholder="1750413802786333"
                    required
                    data-testid="social-app-id-input"
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase block mb-1">
                    App Secret <span className="text-rose-400">(non condividere)</span>
                  </label>
                  <Input
                    type="password"
                    value={appForm.app_secret}
                    onChange={(e) => setAppForm({ ...appForm, app_secret: e.target.value })}
                    placeholder={appConfigured ? "•••••• (già salvato — incolla solo se vuoi cambiarlo)" : "32 caratteri hex"}
                    required={!appConfigured}
                    data-testid="social-app-secret-input"
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full" data-testid="social-save-app-btn">
                  {loading ? "Salvataggio..." : "💾 Salva App ID + Secret"}
                </Button>
              </form>
            </div>
          )}

          {/* STEP 2: OAuth flow */}
          {!isConnected && appConfigured && (
            <div className="bg-slate-800 border border-slate-700 rounded p-4 space-y-3">
              <h3 className="text-white font-medium flex items-center gap-2">
                <span className="text-xs bg-amber-600 text-white px-2 py-0.5 rounded">STEP 2</span>
                Connetti con Facebook
              </h3>
              <p className="text-xs text-slate-400">
                Clicca per autenticarti su Facebook. Verrai reindirizzato al sito di Meta dove dovrai accettare i permessi di Pagina + Instagram. Poi tornerai automaticamente qui.
              </p>
              <Button
                onClick={handleOauthStart}
                disabled={loading}
                className="w-full bg-[#1877F2] hover:bg-[#0d65d9] text-white"
                data-testid="social-oauth-btn"
              >
                <Facebook className="h-4 w-4 mr-2" />
                {loading ? "Apertura Facebook..." : "Connetti con Facebook"}
              </Button>
              <p className="text-xs text-amber-300 mt-2">
                {`⚠️ Prima del primo utilizzo: aggiungi `}<code className="bg-slate-900 px-1 rounded">https://edicolavillaricca.com/api/admin/social/oauth/callback</code>{` nei `}<strong>URI di reindirizzamento OAuth validi</strong>{` dell'app Meta (Casi d'uso → Facebook Login → Personalizza → Impostazioni).`}
              </p>
            </div>
          )}

          {/* STEP 3: Page selector (dopo OAuth) */}
          {!isConnected && oauthPages.length > 0 && (
            <div className="bg-slate-800 border border-emerald-700 rounded p-4 space-y-3">
              <h3 className="text-white font-medium flex items-center gap-2">
                <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded">STEP 3</span>
                Seleziona la Pagina Facebook
              </h3>
              <div className="space-y-2">
                {oauthPages.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-white text-sm cursor-pointer hover:bg-slate-700 p-2 rounded" data-testid={`social-page-choice-${p.id}`}>
                    <input
                      type="radio"
                      name="oauth_page_id"
                      value={p.id}
                      checked={selectedPageId === p.id}
                      onChange={(e) => setSelectedPageId(e.target.value)}
                    />
                    <div>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-slate-400">{p.category || "—"} · ID {p.id}</div>
                    </div>
                  </label>
                ))}
              </div>
              <Button onClick={handleSelectPage} disabled={loading || !selectedPageId} className="w-full" data-testid="social-select-page-btn">
                {loading ? "Collegamento..." : "✅ Conferma pagina selezionata"}
              </Button>
            </div>
          )}

          {/* STEP 3-bis: Manual bind by Page ID (bypass Business Portfolio filter) */}
          {!isConnected && status?.has_user_token && (
            <div className="bg-slate-800 border border-amber-700/60 rounded p-4 space-y-3 mt-3">
              <h3 className="text-white font-medium flex items-center gap-2">
                <span className="text-xs bg-amber-600 text-white px-2 py-0.5 rounded">ALT</span>
                La tua Pagina non è in lista? Inserisci l&apos;ID manualmente
              </h3>
              <p className="text-xs text-slate-400">
                Meta nasconde via OAuth le Pagine collegate a un Business Portfolio.
                Se sei admin della Pagina, forza il collegamento qui inserendo il <strong>Page ID numerico</strong>{" "}
                (es. <code className="bg-slate-900 px-1 rounded">1147473458452745</code>).
              </p>
              <div className="flex gap-2">
                <Input
                  value={manualPageId}
                  onChange={(e) => setManualPageId(e.target.value)}
                  placeholder="Page ID (solo numeri)"
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="social-manual-page-id"
                />
                <Button
                  onClick={handleManualBind}
                  disabled={manualBinding || !manualPageId}
                  className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
                  data-testid="social-manual-bind-btn"
                >
                  {manualBinding ? "Collegamento..." : "🔗 Collega per ID"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Publish test */}
      {isConnected && !isExpired && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">🚀 Pubblica post di test</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePublish} className="space-y-3" data-testid="social-publish-form">
              <div>
                <label className="text-xs text-slate-400 uppercase block mb-1">Image URL (pubblica, https)</label>
                <Input
                  value={pub.image_url}
                  onChange={(e) => setPub({ ...pub, image_url: e.target.value })}
                  placeholder="https://edicolavillaricca.com/static/test.jpg"
                  required
                  data-testid="social-pub-image-input"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase block mb-1">Caption</label>
                <Textarea
                  value={pub.caption}
                  onChange={(e) => setPub({ ...pub, caption: e.target.value })}
                  placeholder="Nuovo arrivo in edicola: ..."
                  rows={3}
                  required
                  data-testid="social-pub-caption-input"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase block mb-1">Product URL (opzionale)</label>
                <Input
                  value={pub.product_url}
                  onChange={(e) => setPub({ ...pub, product_url: e.target.value })}
                  placeholder="https://edicolavillaricca.com/prodotto/..."
                  data-testid="social-pub-url-input"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="flex gap-4 items-center">
                <label className="flex items-center gap-2 text-white text-sm">
                  <input
                    type="checkbox"
                    checked={pub.to_facebook}
                    onChange={(e) => setPub({ ...pub, to_facebook: e.target.checked })}
                    data-testid="social-pub-fb-check"
                  />
                  📘 Facebook
                </label>
                <label className="flex items-center gap-2 text-white text-sm">
                  <input
                    type="checkbox"
                    checked={pub.to_instagram}
                    onChange={(e) => setPub({ ...pub, to_instagram: e.target.checked })}
                    data-testid="social-pub-ig-check"
                  />
                  📸 Instagram
                </label>
              </div>
              <Button type="submit" disabled={publishing} className="w-full" data-testid="social-publish-btn">
                <Send className="h-3 w-3 mr-1" />
                {publishing ? "Pubblicazione in corso..." : "📤 Pubblica adesso"}
              </Button>
            </form>

            {pubResult && (
              <div className="mt-4 space-y-2 text-sm">
                {pubResult.error && (
                  <Alert className="bg-rose-950 border-rose-700">
                    <XCircle className="h-4 w-4 text-rose-400" />
                    <AlertDescription className="text-rose-100">{pubResult.error}</AlertDescription>
                  </Alert>
                )}
                {pubResult.facebook && (
                  <div className={`p-2 rounded ${pubResult.facebook.success ? "bg-emerald-950 text-emerald-200" : "bg-rose-950 text-rose-200"}`} data-testid="social-pub-result-fb">
                    📘 Facebook: {pubResult.facebook.success ? `✅ pubblicato (id ${pubResult.facebook.post_id})` : `❌ ${pubResult.facebook.error}`}
                  </div>
                )}
                {pubResult.instagram && (
                  <div className={`p-2 rounded ${pubResult.instagram.success ? "bg-emerald-950 text-emerald-200" : "bg-rose-950 text-rose-200"}`} data-testid="social-pub-result-ig">
                    📸 Instagram: {pubResult.instagram.success ? `✅ pubblicato (id ${pubResult.instagram.post_id})` : `❌ ${pubResult.instagram.error}`}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Publish existing product by SKU */}
      {isConnected && !isExpired && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">🛒 Pubblica un prodotto dal catalogo</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-400 mb-3">
              Inserisci lo <strong>SKU</strong> del prodotto: foto, titolo, prezzo e descrizione vengono presi
              automaticamente dal catalogo e pubblicati su Facebook e Instagram.
            </p>
            <form onSubmit={handlePublishProduct} className="flex gap-2" data-testid="social-publish-product-form">
              <Input
                value={productSku}
                onChange={(e) => setProductSku(e.target.value)}
                placeholder="SKU del prodotto (es. CORGI-SCANIA-001)"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="social-product-sku"
              />
              <Button
                type="submit"
                disabled={productPublishing || !productSku}
                className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
                data-testid="social-publish-product-btn"
              >
                {productPublishing ? "Pubblicazione..." : "🚀 Pubblica prodotto"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* History */}
      {isConnected && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-white">📋 Storico post pubblicati</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={loadHistory}
                disabled={historyLoading}
                className="text-xs"
                data-testid="social-history-refresh"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Aggiorna
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-sm text-slate-400 italic">Nessun post pubblicato ancora.</p>
            ) : (
              <div className="space-y-2 text-sm" data-testid="social-history-list">
                {history.map((h, i) => {
                  const fbId = h.facebook?.post_id;
                  const igId = h.instagram?.post_id;
                  const pageId = status?.page_id;
                  const fbLink = fbId && pageId ? `https://www.facebook.com/${fbId.replace(pageId + "_", pageId + "/posts/")}` : null;
                  const igLink = igId ? `https://www.instagram.com/p/${igId}` : null;
                  return (
                    <div key={i} className="border border-slate-700 rounded p-2 bg-slate-800/50">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1">
                          <div className="text-white font-medium">
                            {h.article_title || h.article_sku || <em className="text-slate-400">Post di test</em>}
                          </div>
                          <div className="text-xs text-slate-400">
                            {h.posted_at ? new Date(h.posted_at).toLocaleString("it-IT") : "—"}
                            {h.article_sku && ` · SKU: ${h.article_sku}`}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-wrap justify-end">
                          {h.facebook?.success ? (
                            fbLink ? (
                              <a href={fbLink} target="_blank" rel="noreferrer" className="text-xs bg-emerald-900 text-emerald-200 px-2 py-1 rounded hover:bg-emerald-800">📘 FB ✅</a>
                            ) : (
                              <span className="text-xs bg-emerald-900 text-emerald-200 px-2 py-1 rounded">📘 FB ✅</span>
                            )
                          ) : h.facebook ? (
                            <span className="text-xs bg-rose-900 text-rose-200 px-2 py-1 rounded" title={h.facebook?.error || ""}>📘 FB ❌</span>
                          ) : null}
                          {h.instagram?.success ? (
                            igLink ? (
                              <a href={igLink} target="_blank" rel="noreferrer" className="text-xs bg-emerald-900 text-emerald-200 px-2 py-1 rounded hover:bg-emerald-800">📸 IG ✅</a>
                            ) : (
                              <span className="text-xs bg-emerald-900 text-emerald-200 px-2 py-1 rounded">📸 IG ✅</span>
                            )
                          ) : h.instagram ? (
                            <span className="text-xs bg-rose-900 text-rose-200 px-2 py-1 rounded" title={h.instagram?.error || ""}>📸 IG ❌</span>
                          ) : null}
                        </div>
                      </div>
                      {(h.facebook?.error || h.instagram?.error) && (
                        <div className="text-xs text-rose-300 mt-1">
                          {h.facebook?.error && <div>FB: {h.facebook.error}</div>}
                          {h.instagram?.error && <div>IG: {h.instagram.error}</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SocialTab;
